import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Clapperboard, PartyPopper, Radio, Film } from 'lucide-react';

const brands = [
  {
    name: 'LTV Studio',
    tagline: 'Podcast & video production',
    description:
      "London's premier all-inclusive podcast and video studio — multi-camera setups, broadcast audio and full post-production. Miami coming soon.",
    icon: Clapperboard,
    image: '/ltv-studio.jpg',
    link: 'https://www.ltvstudio.co.uk',
    external: true,
    cta: 'Visit the Studio',
    accentText: 'text-amber-400',
    accentBg: 'bg-amber-400/10',
    accentBorder: 'group-hover:border-amber-400/40',
  },
  {
    name: 'LTV Carnival',
    tagline: 'Carnival media worldwide',
    description:
      'Photography, videography, drone footage and live streams from carnival events across the Caribbean, London and beyond.',
    icon: PartyPopper,
    image: 'https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/ma53zcgl_Modern%20Travel%20City%20Vlog%20Photo%20Collage%20YouTube%20Thumbnail-6.png',
    link: 'https://www.ltvcarnival.com',
    external: true,
    cta: 'Enter the Carnival',
    accentText: 'text-fuchsia-400',
    accentBg: 'bg-fuchsia-400/10',
    accentBorder: 'group-hover:border-fuchsia-400/40',
  },
  {
    name: 'LTV Live',
    tagline: 'Live events & streaming',
    description:
      'Concerts, events and shows broadcast in real time — with ticketing, live chat and multi-platform delivery.',
    icon: Radio,
    image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=80',
    link: '/live',
    external: false,
    cta: 'Watch Live',
    accentText: 'text-red-500',
    accentBg: 'bg-red-500/10',
    accentBorder: 'group-hover:border-red-500/40',
  },
  {
    name: 'LTV On Demand',
    tagline: 'Streaming platform',
    description:
      'Exclusive series, concerts, documentaries and behind-the-scenes content — stream anytime, anywhere, in up to 4K.',
    icon: Film,
    image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=1200&q=80',
    link: '/vod',
    external: false,
    cta: 'Start Streaming',
    accentText: 'text-purple-400',
    accentBg: 'bg-purple-400/10',
    accentBorder: 'group-hover:border-purple-400/40',
  },
];

const BrandUniverse = () => {
  const navigate = useNavigate();

  const open = (brand) => {
    if (brand.external) {
      window.open(brand.link, '_blank');
    } else {
      navigate(brand.link);
    }
  };

  return (
    <section id="ltv-universe" className="relative z-10 py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 sm:mb-20">
          <div>
            <p className="ltv-eyebrow text-red-500 mb-4">01 — The LTV Universe</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05]">
              One umbrella.
              <br />
              <span className="text-gray-500">Four worlds.</span>
            </h2>
          </div>
          <p className="text-gray-400 max-w-md text-base sm:text-lg leading-relaxed">
            LTV Production is the hub powering every LTV brand — from our studios to the streets of carnival, from live broadcast to on-demand streaming.
          </p>
        </div>

        {/* Brand grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {brands.map((brand) => {
            const Icon = brand.icon;
            return (
              <button
                key={brand.name}
                onClick={() => open(brand)}
                className={`group relative text-left rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] overflow-hidden transition-all duration-500 ${brand.accentBorder}`}
              >
                {/* Photo */}
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img
                    src={brand.image}
                    alt={brand.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                  <div className={`absolute top-4 left-4 w-11 h-11 rounded-xl backdrop-blur-md flex items-center justify-center ${brand.accentBg} border border-white/10`}>
                    <Icon className={`h-5 w-5 ${brand.accentText}`} />
                  </div>
                  <ArrowUpRight className="absolute top-5 right-5 h-5 w-5 text-white/70 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                </div>

                {/* Copy */}
                <div className="relative p-6 sm:p-8 pt-4 sm:pt-5">
                  <p className={`ltv-eyebrow mb-2 ${brand.accentText}`}>{brand.tagline}</p>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">{brand.name}</h3>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-5 max-w-lg">{brand.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                    {brand.cta}
                    <span className={`block w-8 h-px transition-all duration-300 group-hover:w-12 ${brand.accentText.replace('text-', 'bg-')}`} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BrandUniverse;
